import { GuidedTourDefinition, GuidedTourId, GuidedTourStep } from '../models';

const view = (module: string): GuidedTourStep['requiredAnyOf'] => [{ module, action: 'view' }];
const create = (module: string): GuidedTourStep['requiredAnyOf'] => [{ module, action: 'create' }];

/** Primeira etapa de toda tela: explica para que ela serve (âncora no cabeçalho compartilhado). */
const header = (title: string, description: string, module?: string): GuidedTourStep => ({
  id: 'page.header',
  targetId: 'page.header',
  title,
  description,
  requiredAnyOf: module ? view(module) : undefined
});

const step = (
  id: string,
  title: string,
  description: string,
  requiredAnyOf?: GuidedTourStep['requiredAnyOf']
): GuidedTourStep => ({ id, targetId: id, title, description, requiredAnyOf });

const tour = (id: GuidedTourId, steps: GuidedTourStep[]): GuidedTourDefinition => ({
  id,
  version: 1,
  steps
});

export const CUSTOMERS_TOUR = tour('customers.v1', [
  header(
    'Seus clientes',
    'Cadastre e consulte os clientes da sua empresa em um só lugar.',
    'customers'
  ),
  step(
    'customers.new',
    'Cadastre um cliente',
    'Registre os dados do cliente para usá-lo nas vendas.',
    create('customers')
  ),
  step(
    'customers.filters',
    'Encontre clientes',
    'Filtre por nome ou documento para localizar um cadastro.',
    view('customers')
  )
]);

export const SUPPLIERS_TOUR = tour('suppliers.v1', [
  header('Seus fornecedores', 'Gerencie quem fornece produtos para a sua empresa.', 'suppliers'),
  step(
    'suppliers.new',
    'Cadastre um fornecedor',
    'Registre fornecedores para usá-los nas suas compras.',
    create('suppliers')
  ),
  step(
    'suppliers.filters',
    'Encontre fornecedores',
    'Pesquise por razão social ou nome fantasia.',
    view('suppliers')
  )
]);

export const CATEGORIES_TOUR = tour('categories.v1', [
  header(
    'Categorias de produto',
    'Organize o catálogo em grupos para facilitar buscas e relatórios.',
    'products'
  ),
  step(
    'categories.new',
    'Crie uma categoria',
    'Cadastre categorias para agrupar seus produtos.',
    create('products')
  ),
  step(
    'categories.filters',
    'Encontre categorias',
    'Use os filtros para localizar uma categoria.',
    view('products')
  )
]);

export const BRANDS_TOUR = tour('brands.v1', [
  header(
    'Marcas',
    'Cadastre as marcas para identificar e filtrar os produtos do catálogo.',
    'products'
  ),
  step(
    'brands.new',
    'Crie uma marca',
    'Registre uma marca para usá-la no cadastro de produtos.',
    create('products')
  ),
  step(
    'brands.filters',
    'Encontre marcas',
    'Use os filtros para localizar uma marca.',
    view('products')
  )
]);

export const UNITS_TOUR = tour('units.v1', [
  header('Unidades de medida', 'Defina como cada produto é contado, pesado ou medido.', 'products'),
  step(
    'units.help',
    'Como escolher',
    'Veja exemplos para escolher a unidade certa para cada produto.',
    view('products')
  ),
  step(
    'units.new',
    'Cadastre uma unidade',
    'Crie unidades que ainda não existam para os seus produtos.',
    create('products')
  )
]);

export const USERS_TOUR = tour('users.v1', [
  header('Sua equipe', 'Veja quem tem acesso ao GextorPro e com qual função.', 'users'),
  step(
    'users.new',
    'Convide uma pessoa',
    'Cadastre um usuário e escolha o perfil de acesso dele.',
    create('users')
  ),
  step('users.filters', 'Encontre pessoas', 'Filtre por nome, função ou situação.', view('users'))
]);

export const PROFILES_TOUR = tour('profiles.v1', [
  header('Perfis de acesso', 'Defina o que cada função pode fazer no sistema.', 'users'),
  step(
    'profiles.new',
    'Crie um perfil',
    'Monte um perfil com as permissões que a sua operação precisa.',
    create('users')
  ),
  step(
    'profiles.filters',
    'Encontre perfis',
    'Filtre por tipo ou situação para localizar um perfil.',
    view('users')
  )
]);

export const PERMISSIONS_TOUR = tour('permissions.v1', [
  header(
    'Permissões',
    'Defina o que cada usuário pode acessar, por função ou de forma individual.',
    'users'
  ),
  step(
    'permissions.apply',
    'Aplique em massa',
    'Selecione uma função e aplique o modelo dela a vários usuários de uma vez.',
    [{ module: 'users', action: 'edit' }]
  )
]);

export const PROFILE_TOUR = tour('profile.v1', [
  header('Seu perfil', 'Mantenha seus dados pessoais atualizados.'),
  step('profile.personal', 'Seus dados', 'Atualize seu nome e salve quando terminar.'),
  step('profile.account', 'Dados da conta', 'Consulte o e-mail e as informações da sua conta.')
]);

export const NOTIFICATIONS_TOUR = tour('notifications.v1', [
  header('Notificações', 'Acompanhe os avisos importantes da sua operação.'),
  step(
    'notifications.filters',
    'Filtre os avisos',
    'Veja todas, apenas as não lidas ou as já lidas.'
  ),
  step(
    'notifications.mark-all',
    'Marque como lidas',
    'Limpe a lista de uma vez quando terminar de ler.'
  )
]);

export const BILLING_TOUR = tour('billing.v1', [
  step(
    'billing.current',
    'Seu plano',
    'Veja o plano atual, a situação da assinatura e o uso.',
    view('billing')
  ),
  step(
    'billing.plans',
    'Planos disponíveis',
    'Compare os planos e altere a assinatura quando precisar.',
    view('billing')
  )
]);

export const COMMISSIONS_TOUR = tour('commissions.v1', [
  header(
    'Comissões',
    'Acompanhe valores gerados, fechamentos e pagamentos da equipe.',
    'commissions'
  ),
  step(
    'commissions.close-period',
    'Calcule o período',
    'Calcule as comissões de um período para fechar e pagar a equipe.',
    [{ module: 'commissions', action: 'edit' }]
  ),
  step(
    'commissions.adjustment',
    'Faça ajustes',
    'Corrija um valor registrando um ajuste, com histórico.',
    [{ module: 'commissions', action: 'edit' }]
  )
]);

export const LOYALTY_TOUR = tour('loyalty.v1', [
  header(
    'Programas de fidelidade',
    'Crie e acompanhe programas para recompensar seus clientes.',
    'loyalty'
  ),
  step(
    'loyalty.new',
    'Crie um programa',
    'Defina as regras do programa e as recompensas para seus clientes.',
    create('loyalty')
  )
]);

export const SETTINGS_TOUR = tour('settings.v1', [
  header(
    'Configurações da empresa',
    'Mantenha os dados e as preferências da sua empresa em dia.',
    'settings'
  ),
  step(
    'settings.section',
    'Edite os dados',
    'Altere as informações desta seção e salve quando terminar.',
    view('settings')
  )
]);

const cashView: GuidedTourStep['requiredAnyOf'] = [{ module: 'sales', action: 'cash_view' }];

export const CASHIER_TOUR = tour('cashier.v1', [
  header(
    'Seu caixa',
    'Aqui você abre o caixa, acompanha o turno e confere o dinheiro no fim do dia.',
    'sales'
  ),
  step(
    'cashier.guide',
    'Os 4 passos do caixa',
    'Cadastrar, abrir, vender e fechar. O passo destacado é o seu agora.',
    cashView
  ),
  step(
    'cashier.tab-registers',
    'Vários caixas',
    'Cadastre um caixa por balcão. Quem estiver autorizado abre o que estiver livre.',
    [{ module: 'sales', action: 'cash_manage' }]
  )
]);

export const SCREEN_TOURS: GuidedTourDefinition[] = [
  CASHIER_TOUR,
  CUSTOMERS_TOUR,
  SUPPLIERS_TOUR,
  CATEGORIES_TOUR,
  BRANDS_TOUR,
  UNITS_TOUR,
  USERS_TOUR,
  PROFILES_TOUR,
  PERMISSIONS_TOUR,
  PROFILE_TOUR,
  NOTIFICATIONS_TOUR,
  BILLING_TOUR,
  COMMISSIONS_TOUR,
  LOYALTY_TOUR,
  SETTINGS_TOUR
];

/** Rotas (sem query) que oferecem "Conhecer esta tela" para cada tour. */
export const SCREEN_TOUR_ROUTES: Array<{ tourId: GuidedTourId; match: (path: string) => boolean }> =
  [
    { tourId: 'cashier.v1', match: (path) => path === '/vendas/caixa' },
    { tourId: 'customers.v1', match: (path) => path === '/clientes' },
    { tourId: 'suppliers.v1', match: (path) => path === '/fornecedores' },
    { tourId: 'categories.v1', match: (path) => path === '/categorias-de-produto' },
    { tourId: 'brands.v1', match: (path) => path === '/marcas' },
    { tourId: 'units.v1', match: (path) => path === '/unidades-de-medida' },
    { tourId: 'users.v1', match: (path) => path === '/usuarios' },
    { tourId: 'profiles.v1', match: (path) => path === '/perfis-de-acesso' },
    { tourId: 'permissions.v1', match: (path) => path === '/permissoes' },
    { tourId: 'profile.v1', match: (path) => path === '/perfil' },
    { tourId: 'notifications.v1', match: (path) => path === '/notifications' },
    { tourId: 'billing.v1', match: (path) => path === '/assinatura' },
    { tourId: 'commissions.v1', match: (path) => path === '/comissoes' },
    {
      tourId: 'loyalty.v1',
      match: (path) => path === '/fidelidade' || path === '/fidelidade/programas'
    },
    {
      tourId: 'settings.v1',
      match: (path) => path === '/configuracoes' || path.startsWith('/configuracoes/')
    }
  ];
